import { DateTime } from 'luxon';
import { Timestamp } from 'firebase/firestore';

/**
 * Compute timezone-safe shift timestamps
 * @param {string} localDate - YYYY-MM-DD in location's timezone
 * @param {string} startTime - HH:mm format (e.g., "09:00")
 * @param {string} endTime - HH:mm format (e.g., "17:00")
 * @param {string} timeZone - IANA timezone (e.g., "America/Chicago")
 * @returns {Object} { startAt, endAt, localDateKey, localWeekKey }
 */
export function computeShiftTimestamps(localDate, startTime, endTime, timeZone) {
  // Parse times
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  // Validate IANA timezone format
  if (!timeZone.includes('/')) {
    throw new Error(`Invalid timezone format: ${timeZone}. Expected IANA format like "America/Chicago"`);
  }

  // Parse start time in the location's timezone
  let startDT = DateTime.fromISO(localDate, { zone: timeZone })
    .set({ hour: startHour, minute: startMin, second: 0, millisecond: 0 });

  // Parse end time in the location's timezone
  let endDT = DateTime.fromISO(localDate, { zone: timeZone })
    .set({ hour: endHour, minute: endMin, second: 0, millisecond: 0 });

  // Handle overnight shifts
  if (endDT <= startDT) {
    endDT = endDT.plus({ days: 1 });
  }

  // Compute keys
  const localDateKey = startDT.toISODate(); // YYYY-MM-DD
  const localWeekKey = startDT.toFormat("kkkk-'W'WW"); // YYYY-Www

  return {
    startAt: Timestamp.fromMillis(startDT.toMillis()),
    endAt: Timestamp.fromMillis(endDT.toMillis()),
    localDateKey,
    localWeekKey
  };
}

/**
 * Extract display-friendly local time from a shift
 * @param {Object} shift - Shift document
 * @returns {Object} { localDate, startTime, endTime, timeZone }
 */
export function extractShiftLocalTime(shift) {
  // New format: use timezone-aware fields
  if (shift.startAt && shift.timeZone) {
    const startDT = DateTime.fromMillis(shift.startAt.toMillis(), { zone: shift.timeZone });
    const endDT = DateTime.fromMillis(shift.endAt.toMillis(), { zone: shift.timeZone });
    
    return {
      localDate: startDT.toISODate(),
      startTime: startDT.toFormat('HH:mm'),
      endTime: endDT.toFormat('HH:mm'),
      timeZone: shift.timeZone
    };
  }

  // Legacy fallback: use shiftDate and time strings
  if (shift.shiftDate) {
    const shiftDate = shift.shiftDate.toDate ? shift.shiftDate.toDate() : new Date(shift.shiftDate);
    const localDate = shiftDate.toISOString().split('T')[0];
    
    return {
      localDate,
      startTime: shift.shiftStartTime || '09:00',
      endTime: shift.shiftEndTime || '17:00',
      timeZone: null // Unknown for legacy shifts
    };
  }

  // Fallback to defaults
  return {
    localDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    timeZone: null
  };
}

/**
 * Resolve IANA timezone from location object
 * @param {Object} location - Location document
 * @returns {string} IANA timezone
 */
export function resolveLocationTimezone(location) {
  if (!location) return 'America/Chicago'; // Default fallback
  
  // Check various possible field names
  const tz = location.timeZone || location.timezone || location.tz;
  
  if (!tz) return 'America/Chicago';
  
  // If it's a display name, map to IANA
  if (!tz.includes('/')) {
    return mapDisplayToIANA(tz);
  }
  
  return tz;
}

/**
 * Calculate shift duration in hours
 * @param {Timestamp} startAt 
 * @param {Timestamp} endAt 
 * @returns {number} Hours
 */
export function getShiftDurationHours(startAt, endAt) {
  const startMillis = startAt.toMillis ? startAt.toMillis() : startAt;
  const endMillis = endAt.toMillis ? endAt.toMillis() : endAt;
  const diffHours = (endMillis - startMillis) / (1000 * 60 * 60);
  return Math.round(diffHours * 100) / 100; // Round to 2 decimals
}

/**
 * Check if two shifts overlap
 * @param {Object} shift1 
 * @param {Object} shift2 
 * @returns {boolean}
 */
export function shiftsOverlap(shift1, shift2) {
  const s1Start = shift1.startAt?.toMillis?.() || shift1.startAt;
  const s1End = shift1.endAt?.toMillis?.() || shift1.endAt;
  const s2Start = shift2.startAt?.toMillis?.() || shift2.startAt;
  const s2End = shift2.endAt?.toMillis?.() || shift2.endAt;
  
  return s1Start < s2End && s2Start < s1End;
}

/**
 * Map display timezone names to IANA identifiers
 * @param {string} displayName 
 * @returns {string} IANA timezone
 */
export function mapDisplayToIANA(displayName) {
  const map = {
    'Eastern': 'America/New_York',
    'Central': 'America/Chicago',
    'Mountain': 'America/Denver',
    'Pacific': 'America/Los_Angeles',
    'EST': 'America/New_York',
    'CST': 'America/Chicago',
    'MST': 'America/Denver',
    'PST': 'America/Los_Angeles',
    'EDT': 'America/New_York',
    'CDT': 'America/Chicago',
    'MDT': 'America/Denver',
    'PDT': 'America/Los_Angeles'
  };
  
  return map[displayName] || 'America/Chicago';
}

/**
 * Format a shift for display
 * @param {Object} shift 
 * @returns {string} "9:00 AM - 5:00 PM"
 */
export function formatShiftTime(shift) {
  const { startTime, endTime } = extractShiftLocalTime(shift);
  
  const formatTime = (time) => {
    const [hour, min] = time.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
  };
  
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}
